import { CheckInTemplateType, getTemplateTypeFromId } from '@/constants/checkInTemplates';

/**
 * Structure that defines a summary section
 */
export interface SummarySectionConfig {
  key: string;        // Database key for storing/retrieving summaries
  label: string;      // Display name shown in UI
  promptTemplate?: string;  // Optional custom prompt template
}

/**
 * Full template configuration
 */
export interface TemplateReportConfig {
  sections: SummarySectionConfig[];
  showIndividualUpdates: boolean;
  showBlockerSummary: boolean;
  blockerSummaryLabel?: string;
}

/**
 * Main configuration map - keys are the actual database UUIDs
 * 
 * This maps each template_id directly to its report configuration
 */
const TEMPLATE_CONFIGURATIONS: Record<string, TemplateReportConfig> = {
  // Daily Check-In (c4ca9db0-5ff7-4bfc-9769-3cc39cfbef72)
  'c4ca9db0-5ff7-4bfc-9769-3cc39cfbef72': {
    sections: [
      { key: 'yesterday', label: "Yesterday's Progress" },
      { key: 'today', label: "Today's Focus" },
      { key: 'blockers_overview', label: "Issues Overview" },
    ],
    showIndividualUpdates: true,
    showBlockerSummary: true,
  },
  
  // Daily Standup (638ce109-af5f-45d5-ae8b-cfe2257670be)
  '638ce109-af5f-45d5-ae8b-cfe2257670be': {
    sections: [
      { key: 'team_summary', label: "Team Summary" },
    ],
    showIndividualUpdates: true,
    showBlockerSummary: true,
  },
  
  // Weekly Check-In (a764256e-7163-4569-8517-786a37a75abf)
  'a764256e-7163-4569-8517-786a37a75abf': {
    sections: [
      { key: 'accomplishments', label: "Key Accomplishments" },
      { key: 'challenges', label: "Challenges" },
      { key: 'morale', label: "Morale" },
      { key: 'risks', label: "Risks" },
    ],
    showIndividualUpdates: false,
    showBlockerSummary: true,
    blockerSummaryLabel: "Challenges"
  },
  
  // Bi-Weekly Check-In (b11becdb-c45f-4c82-87c7-01af7a703a1d)
  'b11becdb-c45f-4c82-87c7-01af7a703a1d': {
    sections: [
      { key: 'accomplishments', label: "Key Accomplishments" },
      { key: 'challenges', label: "Challenges" },
      { key: 'morale', label: "Morale" },
      { key: 'risks', label: "Risks" },
    ],
    showIndividualUpdates: false,
    showBlockerSummary: true,
    blockerSummaryLabel: "Challenges"
  },
  
  // Monthly Check-In (62c3ebb0-ff7c-4ed7-926f-2ec695bb85dc)
  '62c3ebb0-ff7c-4ed7-926f-2ec695bb85dc': {
    sections: [
      { key: 'monthly_achievements', label: "Key Achievements" },
      { key: 'monthly_challenges', label: "Challenges" },
      { key: 'monthly_morale', label: "Team Morale" },
      { key: 'monthly_goals', label: "Goals for Next Month" },
    ],
    showIndividualUpdates: false,
    showBlockerSummary: true,
    blockerSummaryLabel: "Challenges"
  },
  
  // New Template ID Configuration (b5d49402-8a7c-4761-9903-2c1a29cafafc) - Assuming this maps to Weekly/Bi-Weekly structure
  'b5d49402-8a7c-4761-9903-2c1a29cafafc': {
    sections: [
      { key: 'accomplishments', label: "Key Accomplishments" },
      { key: 'challenges', label: "Challenges" },
      { key: 'morale', label: "Morale" },
      { key: 'risks', label: "Risks" },
    ],
    showIndividualUpdates: true,
    showBlockerSummary: true,
    blockerSummaryLabel: "Challenges"
  },
  
  // One-Off Check-In (6f5ba2bc-c05c-4425-ae82-aa25f1bfd31a)
  '6f5ba2bc-c05c-4425-ae82-aa25f1bfd31a': {
    sections: [
      { key: 'main_findings', label: "Main Findings" },
      { key: 'action_items', label: "Action Items" },
    ],
    showIndividualUpdates: true,
    showBlockerSummary: false,
  }
};

/**
 * Default configuration for unknown templates
 */
export const DEFAULT_TEMPLATE_CONFIG: TemplateReportConfig = {
  sections: [],
  showIndividualUpdates: true,
  showBlockerSummary: true,
};

/**
 * Get configuration for a template by its ID
 */
export function getTemplateConfig(templateId: string | null | undefined): TemplateReportConfig {
  if (!templateId) return DEFAULT_TEMPLATE_CONFIG;
  return TEMPLATE_CONFIGURATIONS[templateId] || DEFAULT_TEMPLATE_CONFIG;
}

/**
 * Get configuration by template type enum
 * This is a compatibility layer for code that still uses the enum
 */
export function getTemplateConfigByType(templateType: CheckInTemplateType): TemplateReportConfig {
  // Try to find a matching template ID
  const matchingId = Object.keys(TEMPLATE_CONFIGURATIONS).find(id => 
    getTemplateTypeFromId(id) === templateType
  );
  
  return matchingId ? TEMPLATE_CONFIGURATIONS[matchingId] : DEFAULT_TEMPLATE_CONFIG;
} 